import { Component, computed, inject, input, signal, viewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { forkJoin, of } from "rxjs";

import { AdaaHelper } from "../../../../core/utils";
import { dateRangeValidator } from "../../../../core/validators";
import {
  DataTableComponent,
  FormActionButtonsComponent,
  FormCheckboxComponent,
  FormDropdownComponent,
  FormInputComponent,
} from "../../../../shared/components";
import { ContextualHelpContentEditorComponent } from "../../../../shared/components/modals/contextual-help-content-editor/contextual-help-content-editor";
import { Constants } from "../../../../shared/constants/constants";
import { PageMode } from "../../../../shared/constants/enums";
import { TableButtonClicked, ValidatorModelType } from "../../../../shared/models";
import { ContextualHelp, ContextualHelpContent, HelpFormPath } from "../../../../shared/models/online-help-model";
import { ValidatorApiService } from "../../../../shared/services";
import { OnlineHelpService } from "../../../../shared/services/online-help-service";
import { SystemLayoutService } from "../../../../shared/services/system-layout-service";

@Component({
  selector: "adaa-contextual-help-editor",
  imports: [
    DataTableComponent,
    TranslatePipe,
    FormDropdownComponent,
    FormInputComponent,
    FormActionButtonsComponent,
    ReactiveFormsModule,
    FormCheckboxComponent,
    ContextualHelpContentEditorComponent,
  ],
  templateUrl: "./contextual-help-editor.html",
  styleUrl: "./contextual-help-editor.scss",
})
export class ContextualHelpEditorComponent {
  private _formBuilder = inject(FormBuilder);
  private _translateService = inject(TranslateService);
  private _toastrService = inject(ToastrService);
  private _onlineHelpService = inject(OnlineHelpService);
  private _router = inject(Router);
  private _activatedRoute = inject(ActivatedRoute);
  private _systemLayoutService = inject(SystemLayoutService);
  private _validatorApiService = inject(ValidatorApiService);

  formValidation = signal<ValidatorModelType | undefined>(undefined);

  contextualHelpForm: FormGroup = new FormGroup({});
  displayLabel = signal<string>("");
  submitted = signal<boolean>(false);
  pageMode = input.required<string>();
  untilDestroy = AdaaHelper.untilDestroyed();
  PageMode = PageMode;
  categories = signal<HelpFormPath[]>([]);
  categoryOptions = computed(() =>
    AdaaHelper.setDropdownArray(this.categories(), "id", AdaaHelper.getFieldLanguage("name"))
  );
  contextualHelp = signal<ContextualHelp>({
    id: 0,
    formId: 0,
    videoUrlAE: "",
    videoUrlEN: "",
    pageTitleEN: "",
    pageTitleAE: "",
    status: 0,
    updateTs: 0,
    published: "",
    contents: [],
    helpFormPath: {} as HelpFormPath,
  });
  contents = signal<ContextualHelpContent[]>([]);
  contentsFiltered = computed(() => this.contents().filter((item) => item.status !== Constants.OBJECT_STATUS.REMOVE));
  contentId: string | null;
  contentIndex: number | undefined;
  Constants = Constants;
  contentEditor = viewChild.required<ContextualHelpContentEditorComponent>("editor");
  private readonly _untilDestroy = AdaaHelper.untilDestroyed();

  public ngOnInit(): void {
    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroy()).subscribe({
      next: () => {
        if (!AdaaHelper.isPMOEntity()) this._router.navigateByUrl("/console");
      },
    });
    this._prepareForm();
    this._initData();
  }

  private _initData() {
    const id = this._activatedRoute.snapshot?.paramMap?.get("id");
    this.contentId = id;

    forkJoin({
      helpContent: this.contentId ? this._onlineHelpService.getContextualHelpContent(+id!) : of(null),
      categories: this._onlineHelpService.getAllUnpublished(),
    }).subscribe(({ helpContent, categories }) => {
      this.categories.set(categories.responseData);

      if (helpContent?.responseData) {
        this.contextualHelp.set(helpContent.responseData);
        const formPath = this.contextualHelp().helpFormPath;
        if (!this.categories().find((category) => category.id === formPath.id)) {
          this.categories().push(formPath);
        }

        this.contextualHelpForm.patchValue(this.contextualHelp());
        this.contextualHelpForm.get("helpFormPath")?.patchValue(formPath.id);

        const formattedContent = this.contextualHelp().contents.map((item) => ({
          ...item,
          helpContentAE: this._stripPTags(item.helpContentAE),
          helpContentEN: this._stripPTags(item.helpContentEN),
        }));
        this.contents.set(formattedContent);
      }
    });
  }

  private _prepareForm() {
    const isViewMode = this.pageMode() === PageMode.view;

    this.contextualHelpForm = this._formBuilder.group(
      {
        helpFormPath: { value: null, disabled: isViewMode },
        pageTitleAE: { value: null, disabled: isViewMode },
        pageTitleEN: { value: null, disabled: isViewMode },
        videoUrlAE: { value: null, disabled: isViewMode },
        videoUrlEN: { value: null, disabled: isViewMode },
        published: { value: null, disabled: isViewMode },
      },
      {
        validators: Validators.compose([dateRangeValidator("startYear", "endYear")]),
      }
    );

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_CONTEXT_HELP, this.contextualHelpForm)
      .subscribe({
        next: (response) => {
          this.formValidation.set(response.validation);
          this.contextualHelpForm = response.form;
        },
      });
  }

  public checkInvalid(control: string) {
    return (
      this.contextualHelpForm.controls[control].errors?.["required"] &&
      (this.contextualHelpForm.get(control)?.dirty || this.submitted())
    );
  }

  public submit() {
    this.submitted.set(true);
    if (this.contextualHelpForm.invalid) {
      this._toastrService.error(this._translateService.instant("notification.warning.missing_info"));
      return;
    }

    let result = {
      ...(this.pageMode() === PageMode.edit ? { ...this.contextualHelp() } : {}),
      ...this.contextualHelpForm.value,
      contents: this.contents().map((item) => {
        delete item.tempId;
        return item;
      }),
      helpFormPath: this.categories().find((category) => category.id === this.contextualHelpForm.value.helpFormPath),
    };

    result = Object.fromEntries(Object.entries(result).filter(([_, value]) => value !== null));

    this._onlineHelpService.contentUpdate(result).subscribe({
      next: (response) => {
        if (response.inError) return;
        this._toastrService.success(this._translateService.instant("notification.success.save"));
        this.close();
      },
    });
  }

  public close() {
    this._router.navigateByUrl("/console/help/contextual-help");
  }

  public addNew() {
    this.contentEditor().pageMode.set(PageMode.create);
    this.contentEditor().open();
  }

  public getAction(value: TableButtonClicked) {
    this.contentIndex = value.index;
    switch (value.event) {
      case "edit":
        this.contentEditor().contentEditorForm.patchValue(value.data);
        this.contentEditor().pageMode.set(PageMode.edit);
        this.contentEditor().open();
        break;
      case "delete":
        this._deleteContent(value.index);
        break;

      default:
        break;
    }
  }

  private _deleteContent(index: number | undefined) {
    if (!AdaaHelper.isDefinedAndNotEmpty(index)) return;

    const content = this.contentsFiltered()[index];
    const contents = AdaaHelper.clone(this.contents());
    const contentIndex = contents.findIndex((item: ContextualHelpContent) =>
      item.id ? item.id === content.id : item.tempId && item.tempId === content.tempId
    );
    if (contentIndex !== -1) {
      contents[contentIndex].status = Constants.OBJECT_STATUS.REMOVE;
    }
    this.contents.set(contents);
  }

  private _stripPTags(input: string): string {
    const decoded = input.replace(/\\u003c/g, "<").replace(/\\u003e/g, ">");
    const withoutTags = decoded.replace(/<[^>]*>/g, "");
    const text = withoutTags.replace(/&nbsp;/gi, " ").trim();
    return text;
  }

  public setContent(content: ContextualHelpContent) {
    content.helpContentAE = this._stripPTags(content.helpContentAE);
    content.helpContentEN = this._stripPTags(content.helpContentEN);
    const contents = AdaaHelper.clone(this.contents());
    if (AdaaHelper.isDefinedAndNotEmpty(this.contentIndex)) {
      const data = contents[this.contentIndex];
      contents[this.contentIndex] = { ...data, ...content };
    } else {
      contents.push({ ...content, tempId: crypto.randomUUID() });
    }
    this.contents.set(contents);
    this.contentIndex = undefined;
  }
}
